import { useState, useRef, useCallback } from 'react';
import { markdownImportAPI } from '@/lib/api';
import { useApp } from '@/contexts/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FolderOpen, FileText, Check, FolderPlus } from 'lucide-react';
import { toast } from 'sonner';

function parseFrontmatter(content) {
  const match = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { frontmatter: null, body: content };
  const fm = {};
  match[1].split('\n').forEach((line) => {
    const idx = line.indexOf(':');
    if (idx > 0) {
      fm[line.slice(0, idx).trim()] = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
    }
  });
  return { frontmatter: fm, body: match[2] };
}

function extractTitle(filename, content, frontmatter) {
  if (frontmatter?.title) return frontmatter.title;
  const h1 = content.match(/^#\s+(.+)/m);
  if (h1) return h1[1];
  return filename.replace(/\.md$/i, '').replace(/[-_]/g, ' ');
}

const NONE = '__none__';
const NEW = '__new__';

export default function MarkdownImportDialog({ open, onClose }) {
  const { fetchNotes, fetchNotebooks, notebooks } = useApp();
  const [parsed, setParsed] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [newNotebookName, setNewNotebookName] = useState('');
  const [showNewInput, setShowNewInput] = useState(false);
  const [localNotebooks, setLocalNotebooks] = useState([]);
  const folderInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Merge real notebooks with locally-created ones
  const allNotebooks = [
    ...notebooks,
    ...localNotebooks.filter((ln) => !notebooks.some((nb) => nb.name.toLowerCase() === ln.name.toLowerCase())),
  ];

  const processFiles = useCallback(async (fileList) => {
    const mdFiles = Array.from(fileList).filter(
      (f) => f.name.endsWith('.md') || f.name.endsWith('.markdown') || f.name.endsWith('.txt')
    );
    if (mdFiles.length === 0) {
      toast.error('No markdown files found');
      return;
    }

    const items = [];
    for (const file of mdFiles) {
      const text = await file.text();
      const { frontmatter, body } = parseFrontmatter(text);
      const title = extractTitle(file.name, body, frontmatter);
      let notebookName = null;
      if (file.webkitRelativePath) {
        const parts = file.webkitRelativePath.split('/');
        if (parts.length > 2) notebookName = parts[parts.length - 2];
      }
      items.push({
        title,
        content: body,
        notebook_name: notebookName || '',
        filename: file.name,
        path: file.webkitRelativePath || file.name,
        size: file.size,
        hasFrontmatter: !!frontmatter,
      });
    }

    setParsed(items);
    setResult(null);
  }, []);

  const setFileNotebook = (index, value) => {
    setParsed((prev) => prev.map((p, i) => (i === index ? { ...p, notebook_name: value === NONE ? '' : value } : p)));
  };

  const setAllNotebooks = (value) => {
    if (value === NEW) {
      setShowNewInput(true);
      return;
    }
    const name = value === NONE ? '' : value;
    setParsed((prev) => prev.map((p) => ({ ...p, notebook_name: name })));
  };

  const handleCreateNotebook = () => {
    const name = newNotebookName.trim();
    if (!name) return;
    if (!localNotebooks.some((n) => n.name.toLowerCase() === name.toLowerCase()) &&
        !notebooks.some((n) => n.name.toLowerCase() === name.toLowerCase())) {
      setLocalNotebooks((prev) => [...prev, { id: `local_${Date.now()}`, name }]);
    }
    setParsed((prev) => prev.map((p) => ({ ...p, notebook_name: name })));
    setNewNotebookName('');
    setShowNewInput(false);
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    setImporting(true);
    try {
      const res = await markdownImportAPI.import({
        notes: parsed.map((p) => ({
          title: p.title,
          content: p.content,
          notebook_name: p.notebook_name || null,
        })),
        create_notebooks: true,
        skip_duplicates: false,
      });
      setResult(res.data);
      await fetchNotes();
      await fetchNotebooks();
      toast.success(`Imported ${res.data.imported} notes`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const reset = () => {
    setParsed([]);
    setResult(null);
    setLocalNotebooks([]);
    setShowNewInput(false);
    setNewNotebookName('');
  };

  return (
    <Dialog open={open} onOpenChange={() => { reset(); onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[85vh]" data-testid="markdown-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Markdown</DialogTitle>
          <DialogDescription>Import .md files and assign them to notebooks.</DialogDescription>
        </DialogHeader>

        {/* Step 1: File Selection */}
        {parsed.length === 0 && !result && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input ref={fileInputRef} type="file" accept=".md,.markdown,.txt" multiple onChange={(e) => processFiles(e.target.files)} className="hidden" />
                <Button variant="outline" className="w-full h-20 flex-col gap-1.5" onClick={() => fileInputRef.current?.click()} data-testid="md-import-files-btn">
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">Select Files</span>
                </Button>
              </div>
              <div>
                <input ref={folderInputRef} type="file" webkitdirectory="true" onChange={(e) => processFiles(e.target.files)} className="hidden" />
                <Button variant="outline" className="w-full h-20 flex-col gap-1.5" onClick={() => folderInputRef.current?.click()} data-testid="md-import-folder-btn">
                  <FolderOpen className="w-5 h-5" />
                  <span className="text-xs">Select Folder</span>
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Supports .md, .markdown, .txt files. YAML frontmatter parsed for titles.
            </p>
          </div>
        )}

        {/* Step 2: Preview with per-file notebook assignment */}
        {parsed.length > 0 && !result && (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium">{parsed.length} file{parsed.length !== 1 ? 's' : ''} ready</p>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={reset}>Change</Button>
            </div>

            {/* Assign all dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Assign all to:</span>
              <Select onValueChange={setAllNotebooks}>
                <SelectTrigger className="h-7 text-xs flex-1" data-testid="md-assign-all-notebook">
                  <SelectValue placeholder="Choose notebook..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No notebook</SelectItem>
                  {allNotebooks.map((nb) => (
                    <SelectItem key={nb.id} value={nb.name}>{nb.name}</SelectItem>
                  ))}
                  <SelectItem value={NEW}>
                    <span className="flex items-center gap-1.5"><FolderPlus className="w-3 h-3" /> Create new...</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Inline new notebook creator */}
            {showNewInput && (
              <div className="flex items-center gap-2">
                <Input
                  autoFocus
                  value={newNotebookName}
                  onChange={(e) => setNewNotebookName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateNotebook()}
                  placeholder="New notebook name"
                  className="h-7 text-xs flex-1"
                  data-testid="md-new-notebook-input"
                />
                <Button size="sm" className="h-7 px-2 text-xs" onClick={handleCreateNotebook} data-testid="md-new-notebook-create">
                  Create & Assign
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-1.5 text-xs" onClick={() => setShowNewInput(false)}>
                  Cancel
                </Button>
              </div>
            )}

            {/* File list with per-file notebook dropdown */}
            <ScrollArea className="max-h-56 border border-border/40 rounded-lg">
              <div className="divide-y divide-border/30">
                {parsed.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`md-preview-item-${i}`}>
                    <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground truncate">{p.title}</p>
                    </div>
                    <Select value={p.notebook_name || NONE} onValueChange={(v) => {
                      if (v === NEW) { setShowNewInput(true); return; }
                      setFileNotebook(i, v);
                    }}>
                      <SelectTrigger className="h-6 text-[10px] w-32 flex-shrink-0" data-testid={`md-file-notebook-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>No notebook</SelectItem>
                        {allNotebooks.map((nb) => (
                          <SelectItem key={nb.id} value={nb.name}>{nb.name}</SelectItem>
                        ))}
                        <SelectItem value={NEW}>
                          <span className="flex items-center gap-1"><FolderPlus className="w-3 h-3" /> New...</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <Button onClick={handleImport} disabled={importing} className="w-full" data-testid="md-import-confirm-btn">
              {importing ? 'Importing...' : `Import ${parsed.length} Notes`}
            </Button>
          </div>
        )}

        {/* Step 3: Result */}
        {result && (
          <div className="space-y-4 py-2" data-testid="md-import-result">
            <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/5 text-center">
              <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">Import Complete</p>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1.5 border-b border-border/30">
                <span className="text-muted-foreground">Notes imported</span>
                <span className="font-medium">{result.imported}</span>
              </div>
              {result.notebooks_created > 0 && (
                <div className="flex justify-between py-1.5 border-b border-border/30">
                  <span className="text-muted-foreground">Notebooks created</span>
                  <span className="font-medium">{result.notebooks_created}</span>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
                  <p className="text-red-400 font-medium mb-1">Errors:</p>
                  {result.errors.map((e, i) => <p key={e + '-' + i} className="text-red-400/80">{e}</p>)}
                </div>
              )}
            </div>

            <Button variant="outline" className="w-full" onClick={() => { reset(); onClose(); }} data-testid="md-import-done-btn">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
